/**
 * @fileoverview Tema Yardimci Fonksiyonlari ve Sinif Tanimlari
 *
 * Bu modul dark mode destegi icin gerekli CSS sinif kombinasyonlarini
 * ve tema-duyarli yardimci fonksiyonlari saglar.
 *
 * Kullanim sekilleri:
 * 1. Onceden tanimli themeClasses ile (Tailwind dark: prefix kullanir)
 * 2. getThemeClass fonksiyonlari ile (runtime tema secimi icin)
 * 3. getThemeStyle fonksiyonlari ile (inline CSS stilleri icin)
 *
 * @module utils/theme
 *
 * @example
 * // Onceden tanimli siniflar ile:
 * import { themeClasses, tw } from '../utils/theme';
 * <div className={themeClasses.textSecondary}>
 *
 * // tw helper ile birlesik kullanim:
 * <div className={tw(themeClasses.textSecondary, "font-medium", isActive && "underline")}>
 *
 * // Direkt Tailwind dark: prefix ile:
 * <div className="text-black/60 dark:text-white/60">
 *
 * // Runtime tema secimi (isDark parametresi ile):
 * <div className={getThemeClass.textPrimary(isDark)}>
 */

// ==================== Theme Classes ====================
// Tema Siniflari - Tailwind dark: prefix kullanarak otomatik tema gecisi

/**
 * Onceden tanimli tema-duyarli CSS sinif kombinasyonlari.
 * Tailwind CSS dark: variant'i kullanir - sistem temasina gore otomatik gecis yapar.
 *
 * @constant {Object}
 *
 * @property {string} textPrimary - Ana metin rengi (siyah/beyaz)
 * @property {string} textStrong - Vurgulu metin (90% opacity)
 * @property {string} textEmphasis - Vurgulu metin (80% opacity)
 * @property {string} textSecondary - Ikincil metin (60% opacity)
 * @property {string} textTertiary - Ucuncul metin (70% opacity)
 * @property {string} textSubtle - Soluk metin (50% opacity)
 * @property {string} textMuted - Sessiz metin (40% opacity)
 * @property {string} textFaint - Cok soluk metin (30% opacity)
 *
 * @property {string} bgSurface - Ana yuzey arka plani
 * @property {string} bgSecondary - Ikincil arka plan
 * @property {string} bgSubtle - Hafif arka plan (5% opacity)
 * @property {string} bgElevated - Yukseltiłmis arka plan (10% opacity)
 * @property {string} bgHover - Hover durumu arka plani (20% opacity)
 *
 * @property {string} borderDefault - Varsayilan kenar rengi
 * @property {string} borderSubtle - Hafif kenar rengi
 * @property {string} borderStrong - Guclu kenar rengi
 *
 * @property {string} hoverBg - Hover arka plan efekti
 * @property {string} hoverBgStrong - Guclu hover arka plan efekti
 *
 * @property {string} ringDefault - Varsayilan ring/odak rengi
 * @property {string} ringEmphasis - Vurgulu ring rengi
 *
 * @property {string} cardDefault - Standart kart stili
 * @property {string} cardElevated - Yukseltilmis kart stili
 * @property {string} chipDefault - Chip/badge stili
 * @property {string} chipHover - Hover'li chip stili
 * @property {string} inputDefault - Form input stili
 *
 * @example
 * // Metin rengi kullanimi:
 * <p className={themeClasses.textSecondary}>Aciklama metni</p>
 *
 * // Kart kullanimi:
 * <div className={`border rounded-lg ${themeClasses.cardDefault}`}>
 *   Kart icerigi
 * </div>
 */
export const themeClasses = {
  // ==================== Text Colors ====================
  textPrimary: "text-black dark:text-white",
  textStrong: "text-black/90 dark:text-white/90",
  textEmphasis: "text-black/80 dark:text-white/80",
  textSecondary: "text-black/60 dark:text-white/60",
  textTertiary: "text-black/70 dark:text-white/70",
  textSubtle: "text-black/50 dark:text-white/50",
  textMuted: "text-black/40 dark:text-white/40",
  textFaint: "text-black/30 dark:text-white/30",

  // ==================== Background Colors ====================
  bgSurface: "bg-white dark:bg-zinc-900",
  bgSecondary: "bg-white dark:bg-zinc-800",
  bgSubtle: "bg-black/5 dark:bg-white/5",
  bgElevated: "bg-black/10 dark:bg-white/10",
  bgHover: "bg-black/20 dark:bg-white/20",

  // ==================== Border Colors ====================
  borderDefault: "border-black/10 dark:border-white/10",
  borderSubtle: "border-black/5 dark:border-white/5",
  borderStrong: "border-black/20 dark:border-white/20",

  // ==================== Interactive States ====================
  hoverBg: "hover:bg-black/5 dark:hover:bg-white/10",
  hoverBgStrong: "hover:bg-black/10 dark:hover:bg-white/20",

  // ==================== Ring Colors ====================
  ringDefault: "ring-black/10 dark:ring-white/10",
  ringEmphasis: "ring-black/20 dark:ring-white/20",

  // ==================== Composite Patterns ====================
  // Card/container with border
  cardDefault: "border-black/10 dark:border-white/10 bg-white dark:bg-zinc-900",
  cardElevated: "border-black/10 dark:border-white/10 bg-white dark:bg-zinc-800",

  // Chip/badge styling
  chipDefault: "bg-black/5 dark:bg-white/10 text-black/70 dark:text-white/70",
  chipHover: "bg-black/5 dark:bg-white/5 text-black/70 dark:text-white/70 hover:bg-black/10 dark:hover:bg-white/10",

  // Input/form element
  inputDefault: "bg-black/5 dark:bg-white/5 border-black/10 dark:border-white/10",
};

/**
 * Kosullu sinif birlestirme yardimci fonksiyonu.
 * Falsy degerleri filtreler ve bosluk ile birlestirir.
 * clsx/classnames kutuphanelerine benzer lightweight alternatif.
 *
 * @param {...(string|false|null|undefined)} classes - Sinif stringleri veya falsy degerler
 * @returns {string} Birlestirilmis sinif string'i
 *
 * @example
 * // Temel kullanim:
 * tw("base-class", "another-class")
 * // Sonuc: "base-class another-class"
 *
 * @example
 * // Kosullu siniflar:
 * tw("base-class", isActive && "active", isDisabled && "disabled")
 * // isActive=true, isDisabled=false ise: "base-class active"
 *
 * @example
 * // Theme classes ile:
 * tw(themeClasses.textSecondary, "font-medium", selected && "underline")
 */
export function tw(...classes) {
  return classes.filter(Boolean).join(" ");
}

// ==================== isDark-based Utility Functions ====================
// isDark Tabanli Yardimci Fonksiyonlar
// Runtime tema secimi icin kullanilir (ornegin inline stiller veya dark: prefix kullanilamayan durumlar)

/**
 * Tema-duyarli sinif getter fonksiyonlari.
 * isDark parametresine gore uygun sinifi dondurur.
 * Dark: prefix kullanilamayan durumlarda (ornegin dinamik class name) kullanilir.
 *
 * @constant {Object}
 *
 * @property {function(boolean): string} border - Kenar sinifi
 * @property {function(boolean): string} borderStrong - Guclu kenar sinifi
 * @property {function(boolean): string} borderSubtle - Hafif kenar sinifi
 * @property {function(boolean): string} textPrimary - Ana metin sinifi
 * @property {function(boolean): string} textStrong - Vurgulu metin sinifi
 * @property {function(boolean): string} textSecondary - Ikincil metin sinifi
 * @property {function(boolean): string} textMuted - Sessiz metin sinifi
 * @property {function(boolean): string} textFaint - Soluk metin sinifi
 * @property {function(boolean): string} bgPrimary - Ana arka plan sinifi
 * @property {function(boolean): string} bgSecondary - Ikincil arka plan sinifi
 * @property {function(boolean): string} bgSubtle - Hafif arka plan sinifi
 * @property {function(boolean): string} bgElevated - Yukseltilmis arka plan sinifi
 * @property {function(boolean): string} bgHover - Hover arka plan sinifi
 * @property {function(boolean): string} cardBase - Temel kart sinifi
 * @property {function(boolean): string} cardHover - Hover'li kart sinifi
 * @property {function(boolean): string} bgTertiary - Ucuncul arka plan sinifi
 * @property {function(boolean): string} closeButtonOverlay - Kapat butonu overlay sinifi
 *
 * @example
 * // Dinamik sinif kullanimi:
 * const borderClass = getThemeClass.border(isDark);
 * // isDark=true ise: "border-white/10"
 * // isDark=false ise: "border-black/10"
 *
 * @example
 * // Kart komponenti:
 * <div className={`rounded-lg ${getThemeClass.cardBase(isDark)}`}>
 */
export const getThemeClass = {
  // Borders
  border: (isDark) => isDark ? "border-white/10" : "border-black/10",
  borderStrong: (isDark) => isDark ? "border-white/20" : "border-black/20",
  borderSubtle: (isDark) => isDark ? "border-white/5" : "border-black/5",

  // Text
  textPrimary: (isDark) => isDark ? "text-white" : "text-black",
  textStrong: (isDark) => isDark ? "text-white/90" : "text-black/90",
  textSecondary: (isDark) => isDark ? "text-white/60" : "text-black/60",
  textMuted: (isDark) => isDark ? "text-white/40" : "text-black/40",
  textFaint: (isDark) => isDark ? "text-white/30" : "text-black/30",

  // Background
  bgPrimary: (isDark) => isDark ? "bg-zinc-900" : "bg-white",
  bgSecondary: (isDark) => isDark ? "bg-zinc-800" : "bg-gray-50",
  bgSubtle: (isDark) => isDark ? "bg-white/5" : "bg-black/5",
  bgElevated: (isDark) => isDark ? "bg-white/10" : "bg-black/10",
  bgHover: (isDark) => isDark ? "hover:bg-white/10" : "hover:bg-black/10",

  // Card patterns
  cardBase: (isDark) => isDark
    ? "bg-zinc-900 border-white/10"
    : "bg-white border-black/10",
  cardHover: (isDark) => isDark
    ? "bg-zinc-900 border-white/10 hover:bg-zinc-800"
    : "bg-white border-black/10 hover:bg-gray-50",

  // Map/container backgrounds
  bgTertiary: (isDark) => isDark ? "bg-zinc-800" : "bg-gray-100",

  // Floating close button overlay
  closeButtonOverlay: (isDark) => isDark
    ? "bg-black/60 text-white hover:bg-black/80"
    : "bg-white/90 text-black hover:bg-white shadow-md",
};

/**
 * Tema-duyarli inline stil degerleri.
 * CSS-in-JS veya style prop'lari icin kullanilir.
 * Tailwind siniflari kullanilamayan durumlarda (ornegin Mapbox marker'lari) gereklidir.
 *
 * @constant {Object}
 *
 * @property {function(boolean): string} bgPrimary - Ana arka plan rengi (hex)
 * @property {function(boolean): string} bgSecondary - Ikincil arka plan rengi (hex)
 * @property {function(boolean): string} textPrimary - Ana metin rengi (hex)
 * @property {function(boolean): string} textSecondary - Ikincil metin rengi (rgba)
 * @property {function(boolean): string} textMuted - Sessiz metin rengi (rgba)
 * @property {function(boolean): string} borderDefault - Varsayilan kenar rengi (rgba)
 * @property {function(boolean): string} borderStrong - Guclu kenar rengi (rgba)
 *
 * @example
 * // Inline style kullanimi:
 * <div style={{ backgroundColor: getThemeStyle.bgPrimary(isDark) }}>
 *
 * @example
 * // Mapbox marker stili:
 * const markerEl = document.createElement('div');
 * markerEl.style.backgroundColor = getThemeStyle.bgPrimary(isDark);
 * markerEl.style.color = getThemeStyle.textPrimary(isDark);
 */
export const getThemeStyle = {
  // Background colors
  bgPrimary: (isDark) => isDark ? '#18181b' : '#ffffff',
  bgSecondary: (isDark) => isDark ? '#27272a' : '#f9fafb',

  // Text colors
  textPrimary: (isDark) => isDark ? '#ffffff' : '#000000',
  textSecondary: (isDark) => isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
  textMuted: (isDark) => isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',

  // Border colors
  borderDefault: (isDark) => isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
  borderStrong: (isDark) => isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
};
